import React, { useState } from "react"

/**
 * Factories management content for the secondary sidebar: add form + list of
 * runs/items with links and delete. Renders inside GenericSecondarySidebar.
 */
function FactoriesSecondarySidebarContent({
  items = [],
  onAddItem,
  onDeleteItem
}) {
  const [newItemName, setNewItemName] = useState("")
  const [hoveredItemId, setHoveredItemId] = useState(null)

  const handleSubmit = (e) => {
    e.preventDefault()
    if (newItemName.trim() && onAddItem) {
      onAddItem(newItemName.trim())
      setNewItemName("")
    }
  }

  const formatDate = (dateString, createdAt) => {
    if (createdAt) return createdAt
    if (!dateString) return ""
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffTime = Math.abs(now - date)
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
      if (diffDays === 0) return "Today"
      if (diffDays === 1) return "1 day ago"
      return `${diffDays} day(s) ago`
    } catch (e) {
      return dateString
    }
  }

  return (
    <>
      <form
        id="secondary-left-sidebar-add-form"
        onSubmit={handleSubmit}
        className="flex flex-col w-full mb-2 space-y-1"
      >
        <div id="secondary-left-sidebar-add-form-container" className="flex space-x-1 group rounded-md">
          <div id="secondary-left-sidebar-add-input-container" className="w-full flex flex-col relative border-gray-200 focus-within:border-indigo-300 focus-within:ring focus-within:ring-indigo-200 focus-within:ring-opacity-50 transition-all duration-200">
            <input
              id="secondary-left-sidebar-add-input"
              placeholder="New Factory Name"
              type="text"
              name="name"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              className="w-full rounded-md px-3 py-2 border border-gray-300 outline-none focus:outline-none placeholder:text-gray-400 text-sm"
              disabled={!onAddItem}
            />
          </div>
          <button
            id="secondary-left-sidebar-add-button"
            type="submit"
            className="p-0 m-0"
            disabled={!onAddItem}
          >
            <div id="secondary-left-sidebar-add-button-inner" className="cursor-pointer flex justify-between relative rounded-md overflow-hidden group relative hover:bg-gradient-to-r hover:from-gray-200 hover:to-gray-50 hover:ring-2 hover:ring-offset-2 hover:ring-gray-600 transition-all ease-out duration-300 rounded-md text-sm p-1.5">
              <span className="absolute right-0 w-8 h-32 -mt-12 transition-all duration-500 transform translate-x-12 bg-gray-400 opacity-10 rotate-12 group-hover:-translate-x-40 ease" />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                aria-hidden="true"
                data-slot="icon"
                className="w-5 h-5"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </div>
          </button>
        </div>
      </form>

      <div
        id="secondary-left-sidebar-separator-container"
        style={{
          width: "100%",
          marginBottom: "8px",
          boxSizing: "border-box",
          border: "0px solid rgb(229, 231, 235)",
          flexShrink: 0
        }}
      >
        <div
          id="secondary-left-sidebar-separator"
          style={{
            backgroundImage: "linear-gradient(to right, rgba(0, 0, 0, 0), rgb(229, 231, 235), rgba(0, 0, 0, 0))",
            height: "1px",
            boxSizing: "border-box",
            border: "0px solid rgb(229, 231, 235)"
          }}
        />
      </div>

      <div
        id="secondary-left-sidebar-items-container"
        style={{
          width: "100%",
          flex: "1 1 0%",
          minHeight: "0px",
          boxSizing: "border-box",
          border: "0px solid rgb(229, 231, 235)",
          overflowY: "auto",
          overflowX: "hidden"
        }}
      >
        <div
          id="secondary-left-sidebar-items-list"
          style={{
            width: "100%",
            boxSizing: "border-box",
            border: "0px solid rgb(229, 231, 235)"
          }}
        >
          {items.map((item, index) => {
            const itemId = item.id || index
            const isHovered = hoveredItemId === itemId
            return (
              <div
                key={item.id || index}
                id={`secondary-left-sidebar-item-${index}`}
                className="space-y-0.5 w-full"
                style={{
                  marginTop: index > 0 ? "2px" : "0px",
                  marginBottom: "0px",
                  width: "100%",
                  boxSizing: "border-box",
                  border: "0px solid rgb(229, 231, 235)"
                }}
                onMouseEnter={() => setHoveredItemId(itemId)}
                onMouseLeave={() => setHoveredItemId(null)}
              >
                <div id={`secondary-left-sidebar-item-content-${index}`} className="w-full">
                  <div
                    id={`secondary-left-sidebar-item-link-container-${index}`}
                    className="flex space-x-1 w-full rounded-md mb-1 group transition-all duration-200 ease-in-out"
                    style={{
                      transform: isHovered ? "translateX(0.25rem)" : "translateX(0)",
                      transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
                      transitionDuration: "0.2s",
                      transitionProperty: "all",
                      borderRadius: "6px",
                      width: "100%",
                      display: "flex",
                      marginBottom: "4px",
                      boxSizing: "border-box",
                      border: "0px solid rgb(229, 231, 235)"
                    }}
                  >
                    <a
                      id={`secondary-left-sidebar-item-link-${index}`}
                      href={item.href || item.url || "#"}
                      className="flex lg:gap-x-1 gap-x-1 rounded-md text-sm font-semibold lg:w-full items-center min-w-0"
                      style={{
                        columnGap: "4px",
                        width: "100%",
                        fontWeight: 600,
                        fontSize: "14px",
                        lineHeight: "20px",
                        borderRadius: "6px",
                        alignItems: "center",
                        minWidth: "0px",
                        display: "flex",
                        color: "rgb(0, 0, 0)",
                        textDecoration: "none",
                        boxSizing: "border-box",
                        border: "0px solid rgb(229, 231, 235)"
                      }}
                    >
                      <div
                        id={`secondary-left-sidebar-item-link-inner-${index}`}
                        className="cursor-pointer flex justify-between relative rounded-md overflow-hidden group relative transition-all ease-out duration-300 flex gap-2 rounded-md transition-all duration-200 w-full text-indigo-600 text-start"
                        style={{
                          backgroundImage: "none",
                          backgroundColor: "transparent",
                          borderLeft: "4px solid transparent",
                          boxShadow: "none",
                          transitionTimingFunction: "cubic-bezier(0, 0, 0.2, 1)",
                          transitionDuration: "0.3s",
                          transitionProperty: "all",
                          color: document.documentElement.classList.contains("dark") ? "rgb(209, 213, 219)" : "rgb(55, 65, 81)",
                          fontWeight: 500,
                          fontSize: "14px",
                          lineHeight: "20px",
                          padding: "8px",
                          borderRadius: "6px",
                          overflow: "hidden",
                          gap: "8px",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          width: "100%",
                          display: "flex",
                          position: "relative",
                          boxSizing: "border-box"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundImage = "linear-gradient(to right, rgb(229, 231, 235), rgb(249, 250, 251))"
                          e.currentTarget.style.borderLeft = "4px solid rgb(99, 102, 241)"
                          e.currentTarget.style.boxShadow = "0 0 0 2px rgb(255, 255, 255), 0 0 0 4px rgb(75, 85, 99), 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)"
                          e.currentTarget.style.color = "rgb(79, 70, 229)"
                          const iconDiv = e.currentTarget.querySelector(".text-indigo-600")
                          if (iconDiv) iconDiv.style.color = "rgb(79, 70, 229)"
                          const dateSpan = e.currentTarget.querySelector(".text-xs")
                          if (dateSpan) dateSpan.style.color = "rgb(129, 140, 248)"
                          const shimmer = e.currentTarget.querySelector("span.absolute")
                          if (shimmer) shimmer.style.transform = "translateX(-10rem) rotate(12deg)"
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundImage = "none"
                          e.currentTarget.style.backgroundColor = "transparent"
                          e.currentTarget.style.borderLeft = "4px solid transparent"
                          e.currentTarget.style.boxShadow = "none"
                          e.currentTarget.style.color = "rgb(55, 65, 81)"
                          const iconDiv = e.currentTarget.querySelector(".text-indigo-600")
                          if (iconDiv) iconDiv.style.color = "rgb(107, 114, 128)"
                          const dateSpan = e.currentTarget.querySelector(".text-xs")
                          if (dateSpan) dateSpan.style.color = "rgb(156, 163, 175)"
                          const shimmer = e.currentTarget.querySelector("span.absolute")
                          if (shimmer) shimmer.style.transform = "translateX(3rem) rotate(12deg)"
                        }}
                      >
                        <span
                          className="absolute right-0 w-8 h-32 -mt-12 transition-all duration-500 transform ease"
                          style={{
                            transitionDuration: "0.5s",
                            transitionProperty: "all",
                            transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
                            opacity: "0.1",
                            backgroundColor: "rgb(156, 163, 175)",
                            transform: "translateX(3rem) rotate(12deg)",
                            width: "2rem",
                            height: "128px",
                            marginTop: "-48px",
                            right: "0px",
                            position: "absolute",
                            boxSizing: "border-box",
                            border: "0px solid rgb(229, 231, 235)"
                          }}
                        />
                        <div
                          className="text-indigo-600 transition-colors duration-200"
                          style={{
                            transitionDuration: "0.2s",
                            transitionProperty: "color",
                            color: document.documentElement.classList.contains("dark") ? "rgb(156, 163, 175)" : "rgb(107, 114, 128)",
                            boxSizing: "border-box",
                            border: "0px solid rgb(229, 231, 235)"
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            aria-hidden="true"
                            data-slot="icon"
                            style={{ width: "1.5rem", height: "24px", display: "block", verticalAlign: "middle" }}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-1.757 4.306 4.493 4.493 0 0 0 4.306-1.758M16.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
                          </svg>
                        </div>
                        <div
                          className="flex flex-col flex-1 min-w-0 text-start"
                          style={{
                            textAlign: "start",
                            flexDirection: "column",
                            flex: "1 1 0%",
                            minWidth: "0px",
                            display: "flex",
                            boxSizing: "border-box",
                            border: "0px solid rgb(229, 231, 235)"
                          }}
                        >
                          <span
                            className="font-semibold"
                            style={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              boxSizing: "border-box",
                              border: "0px solid rgb(229, 231, 235)"
                            }}
                          >
                            {item.name || item.title}
                          </span>
                          <span
                            className="text-xs break-words"
                            style={{
                              color: "rgb(156, 163, 175)",
                              fontSize: "12px",
                              lineHeight: "16px",
                              overflowWrap: "break-word",
                              boxSizing: "border-box",
                              border: "0px solid rgb(229, 231, 235)"
                            }}
                          >
                            {formatDate(item.date || item.dateString, item.createdAt)}
                          </span>
                        </div>
                      </div>
                    </a>
                    {onDeleteItem && (
                      <div
                        id={`secondary-left-sidebar-item-delete-container-${index}`}
                        className="opacity-100 transition-opacity duration-200 flex items-center"
                        style={{
                          transitionDuration: "0.2s",
                          transitionProperty: "opacity",
                          opacity: isHovered ? 1 : 0,
                          marginRight: "0px",
                          marginLeft: "4px",
                          alignItems: "center",
                          display: "flex",
                          boxSizing: "border-box",
                          border: "0px solid rgb(229, 231, 235)"
                        }}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            onDeleteItem(item.id || index)
                          }}
                          style={{
                            cursor: "pointer",
                            appearance: "button",
                            backgroundColor: "rgba(0, 0, 0, 0)",
                            backgroundImage: "none",
                            textTransform: "none",
                            fontFamily: 'Inter, "Inter Fallback"',
                            fontSize: "16px",
                            fontWeight: 400,
                            lineHeight: "24px",
                            color: "rgb(0, 0, 0)",
                            margin: "0px",
                            padding: "0px",
                            boxSizing: "border-box",
                            border: "0px solid rgb(229, 231, 235)"
                          }}
                        >
                          <div
                            className="cursor-pointer flex justify-between relative rounded-md overflow-hidden group relative transition-all ease-out duration-300 p-2 w-full"
                            style={{
                              transitionTimingFunction: "cubic-bezier(0, 0, 0.2, 1)",
                              transitionDuration: "0.3s",
                              transitionProperty: "all",
                              color: "rgb(229, 231, 235)",
                              padding: "8px",
                              borderRadius: "6px",
                              overflow: "hidden",
                              justifyContent: "space-between",
                              cursor: "pointer",
                              width: "100%",
                              display: "flex",
                              position: "relative",
                              boxSizing: "border-box",
                              border: "0px solid rgb(229, 231, 235)"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundImage = "linear-gradient(to right, rgb(254, 202, 202), rgb(254, 226, 226))"
                              e.currentTarget.style.boxShadow = "0 0 0 2px rgb(255, 255, 255), 0 0 0 4px rgb(248, 113, 113), 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)"
                              e.currentTarget.style.color = "rgb(239, 68, 68)"
                              const svg = e.currentTarget.querySelector("svg")
                              if (svg) svg.style.color = "rgb(239, 68, 68)"
                              const shimmer = e.currentTarget.querySelector("span")
                              if (shimmer) shimmer.style.transform = "translateX(-10rem) rotate(12deg)"
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundImage = "none"
                              e.currentTarget.style.boxShadow = "none"
                              e.currentTarget.style.color = "rgb(229, 231, 235)"
                              const svg = e.currentTarget.querySelector("svg")
                              if (svg) svg.style.color = "rgb(229, 231, 235)"
                              const shimmer = e.currentTarget.querySelector("span")
                              if (shimmer) shimmer.style.transform = "translateX(3rem) rotate(12deg)"
                            }}
                          >
                            <span
                              className="absolute right-0 w-8 h-32 -mt-12 transition-all duration-500 transform ease"
                              style={{
                                transitionDuration: "0.5s",
                                transitionProperty: "all",
                                transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
                                opacity: "0.1",
                                backgroundColor: "rgb(254, 202, 202)",
                                transform: "translateX(3rem) rotate(12deg)",
                                width: "2rem",
                                height: "128px",
                                marginTop: "-48px",
                                right: "0px",
                                position: "absolute",
                                boxSizing: "border-box",
                                border: "0px solid rgb(229, 231, 235)"
                              }}
                            />
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth="1.5"
                              stroke="currentColor"
                              className="w-6 h-6"
                              style={{
                                width: "1.5rem",
                                height: "24px",
                                display: "block",
                                verticalAlign: "middle",
                                boxSizing: "border-box",
                                color: "rgb(229, 231, 235)",
                                transition: "color 0.3s ease"
                              }}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                            </svg>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}

export default FactoriesSecondarySidebarContent
